<template>
  <DashboardLayout>
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center space-x-3">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
              Payment Testing
            </h1>
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
              DEVELOPMENT ONLY
            </span>
          </div>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Test payment flows and webhook events in development mode
          </p>
        </div>
        <Link :href="route('settings.billing.index')">
          <Button variant="secondary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Billing
          </Button>
        </Link>
      </div>

      <!-- Warning Banner -->
      <Alert variant="warning">
        <template #title>Development Mode Only</template>
        This page is only visible in development/testing environments. It provides tools to test payment flows
        without affecting production data. All test cards use Stripe's test mode.
      </Alert>

      <!-- Test Card Numbers -->
      <Card>
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Stripe Test Card Numbers
        </h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Use these test card numbers with any future expiration date and any 3-digit CVC.
        </p>

        <div class="space-y-4">
          <!-- Success Card -->
          <div class="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="font-semibold text-gray-900 dark:text-white">Success</span>
              </div>
              <code class="text-lg font-mono text-gray-900 dark:text-white">4242 4242 4242 4242</code>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Payment will succeed without additional authentication
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              @click="copyToClipboard('4242424242424242')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>

          <!-- Decline Card -->
          <div class="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="font-semibold text-gray-900 dark:text-white">Generic Decline</span>
              </div>
              <code class="text-lg font-mono text-gray-900 dark:text-white">4000 0000 0000 0002</code>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Payment will be declined with a generic decline code
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              @click="copyToClipboard('4000000000000002')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>

          <!-- 3D Secure Card -->
          <div class="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span class="font-semibold text-gray-900 dark:text-white">3D Secure Required</span>
              </div>
              <code class="text-lg font-mono text-gray-900 dark:text-white">4000 0025 0000 3155</code>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Requires additional 3D Secure authentication
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              @click="copyToClipboard('4000002500003155')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>

          <!-- Insufficient Funds -->
          <div class="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="font-semibold text-gray-900 dark:text-white">Insufficient Funds</span>
              </div>
              <code class="text-lg font-mono text-gray-900 dark:text-white">4000 0000 0000 9995</code>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Declined with insufficient funds error
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              @click="copyToClipboard('4000000000009995')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>

          <!-- Expired Card -->
          <div class="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
            <div class="flex-1">
              <div class="flex items-center space-x-2 mb-2">
                <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="font-semibold text-gray-900 dark:text-white">Expired Card</span>
              </div>
              <code class="text-lg font-mono text-gray-900 dark:text-white">4000 0000 0000 0069</code>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Declined because the card has expired
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              @click="copyToClipboard('4000000000000069')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </Button>
          </div>
        </div>

        <div class="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            <strong>💡 Tip:</strong> Use any future expiration date (e.g., 12/30) and any 3-digit CVC (e.g., 123).
            ZIP code can be any valid format.
          </p>
        </div>
      </Card>

      <!-- Test Scenarios -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Payment Testing -->
        <Card>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Payment Testing
          </h2>

          <div class="space-y-3">
            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="triggerPaymentSuccess"
              :loading="testingPaymentSuccess"
            >
              <svg class="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Trigger Payment Success
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="triggerPaymentFailure"
              :loading="testingPaymentFailure"
            >
              <svg class="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Trigger Payment Failure
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="testRefund"
              :loading="testingRefund"
            >
              <svg class="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Test Refund Flow
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="testProration"
              :loading="testingProration"
            >
              <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Test Proration Calculation
            </Button>
          </div>
        </Card>

        <!-- Webhook Testing -->
        <Card>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Webhook Testing
          </h2>

          <div class="space-y-3">
            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="simulateWebhook('payment_intent.succeeded')"
              :loading="simulatingWebhook === 'payment_intent.succeeded'"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Simulate: payment_intent.succeeded
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="simulateWebhook('payment_intent.failed')"
              :loading="simulatingWebhook === 'payment_intent.failed'"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Simulate: payment_intent.failed
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="simulateWebhook('customer.subscription.updated')"
              :loading="simulatingWebhook === 'customer.subscription.updated'"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Simulate: subscription.updated
            </Button>

            <Button
              variant="secondary"
              class="w-full justify-start"
              @click="simulateWebhook('invoice.payment_succeeded')"
              :loading="simulatingWebhook === 'invoice.payment_succeeded'"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Simulate: invoice.payment_succeeded
            </Button>
          </div>
        </Card>
      </div>

      <!-- Subscription Testing -->
      <Card>
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Subscription Testing
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="secondary"
            @click="fastForwardBillingCycle"
            :loading="fastForwarding"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            Fast-Forward Billing
          </Button>

          <Button
            variant="secondary"
            @click="triggerRenewal"
            :loading="triggeringRenewal"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Trigger Renewal
          </Button>

          <Button
            variant="secondary"
            @click="testPlanChange"
            :loading="testingPlanChange"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Test Plan Change
          </Button>

          <Button
            variant="secondary"
            @click="testCancellation"
            :loading="testingCancellation"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Test Cancellation
          </Button>
        </div>

        <div class="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p class="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>⚠️ Warning:</strong> These actions will modify test subscription data.
            Make sure you're using a test company and test subscription.
          </p>
        </div>
      </Card>

      <!-- Test Results -->
      <Card v-if="testResults.length > 0">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            Test Results
          </h2>
          <Button size="sm" variant="secondary" @click="clearResults">
            Clear Results
          </Button>
        </div>

        <div class="space-y-3">
          <div
            v-for="(result, index) in testResults"
            :key="index"
            class="p-4 rounded-lg border"
            :class="[
              result.success
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            ]"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-2">
                  <svg
                    v-if="result.success"
                    class="w-5 h-5 text-green-600 dark:text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <svg
                    v-else
                    class="w-5 h-5 text-red-600 dark:text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="font-semibold text-gray-900 dark:text-white">
                    {{ result.test }}
                  </span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ result.timestamp }}
                  </span>
                </div>
                <p class="text-sm text-gray-700 dark:text-gray-300">
                  {{ result.message }}
                </p>
                <pre
                  v-if="result.data"
                  class="mt-2 p-2 bg-gray-900 dark:bg-gray-950 text-gray-100 text-xs rounded overflow-x-auto"
                >{{ JSON.stringify(result.data, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import { useToast } from '@/Composables/useToast';

// Composables
const { success, error } = useToast();

// Refs
const testingPaymentSuccess = ref(false);
const testingPaymentFailure = ref(false);
const testingRefund = ref(false);
const testingProration = ref(false);
const simulatingWebhook = ref(null);
const fastForwarding = ref(false);
const triggeringRenewal = ref(false);
const testingPlanChange = ref(false);
const testingCancellation = ref(false);
const testResults = ref([]);

// Methods
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    success('Card number copied to clipboard');
  } catch (err) {
    error('Failed to copy to clipboard');
  }
};

const addTestResult = (test, message, isSuccess = true, data = null) => {
  testResults.value.unshift({
    test,
    message,
    success: isSuccess,
    data,
    timestamp: new Date().toLocaleTimeString(),
  });
};

const clearResults = () => {
  testResults.value = [];
};

const triggerPaymentSuccess = async () => {
  testingPaymentSuccess.value = true;
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Payment Success',
      'Payment processed successfully using test card 4242 4242 4242 4242',
      true,
      { amount: 99.99, status: 'succeeded' }
    );
    success('Payment test completed');
  } catch (err) {
    addTestResult('Payment Success', 'Test failed: ' + err.message, false);
    error('Payment test failed');
  } finally {
    testingPaymentSuccess.value = false;
  }
};

const triggerPaymentFailure = async () => {
  testingPaymentFailure.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Payment Failure',
      'Payment declined using test card 4000 0000 0000 0002',
      true,
      { error_code: 'card_declined', decline_code: 'generic_decline' }
    );
    success('Payment failure test completed');
  } catch (err) {
    addTestResult('Payment Failure', 'Test failed: ' + err.message, false);
    error('Payment failure test failed');
  } finally {
    testingPaymentFailure.value = false;
  }
};

const testRefund = async () => {
  testingRefund.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Refund Flow',
      'Refund processed successfully',
      true,
      { refund_amount: 99.99, status: 'succeeded' }
    );
    success('Refund test completed');
  } catch (err) {
    addTestResult('Refund Flow', 'Test failed: ' + err.message, false);
    error('Refund test failed');
  } finally {
    testingRefund.value = false;
  }
};

const testProration = async () => {
  testingProration.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Proration Calculation',
      'Proration calculated for plan upgrade',
      true,
      { unused_credit: 15.50, new_charge: 30.00, net_charge: 14.50 }
    );
    success('Proration test completed');
  } catch (err) {
    addTestResult('Proration Calculation', 'Test failed: ' + err.message, false);
    error('Proration test failed');
  } finally {
    testingProration.value = false;
  }
};

const simulateWebhook = async (eventType) => {
  simulatingWebhook.value = eventType;
  try {
    await new Promise(resolve => setTimeout(resolve, 1500));
    addTestResult(
      `Webhook: ${eventType}`,
      `Webhook event simulated successfully`,
      true,
      { event_type: eventType, received_at: new Date().toISOString() }
    );
    success(`Webhook ${eventType} simulated`);
  } catch (err) {
    addTestResult(`Webhook: ${eventType}`, 'Simulation failed: ' + err.message, false);
    error('Webhook simulation failed');
  } finally {
    simulatingWebhook.value = null;
  }
};

const fastForwardBillingCycle = async () => {
  fastForwarding.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Fast-Forward Billing',
      'Billing cycle advanced to end of period',
      true,
      { new_period_end: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
    );
    success('Billing cycle fast-forwarded');
  } catch (err) {
    addTestResult('Fast-Forward Billing', 'Test failed: ' + err.message, false);
    error('Fast-forward failed');
  } finally {
    fastForwarding.value = false;
  }
};

const triggerRenewal = async () => {
  triggeringRenewal.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1500));
    addTestResult(
      'Trigger Renewal',
      'Subscription renewal triggered successfully',
      true,
      { renewed_at: new Date().toISOString(), next_billing: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() }
    );
    success('Renewal triggered');
  } catch (err) {
    addTestResult('Trigger Renewal', 'Test failed: ' + err.message, false);
    error('Renewal trigger failed');
  } finally {
    triggeringRenewal.value = false;
  }
};

const testPlanChange = async () => {
  testingPlanChange.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Plan Change',
      'Plan change simulated with proration',
      true,
      { old_plan: 'Basic', new_plan: 'Professional', proration: 14.50 }
    );
    success('Plan change test completed');
  } catch (err) {
    addTestResult('Plan Change', 'Test failed: ' + err.message, false);
    error('Plan change test failed');
  } finally {
    testingPlanChange.value = false;
  }
};

const testCancellation = async () => {
  testingCancellation.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    addTestResult(
      'Cancellation',
      'Subscription cancellation simulated',
      true,
      { status: 'cancelled', ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() }
    );
    success('Cancellation test completed');
  } catch (err) {
    addTestResult('Cancellation', 'Test failed: ' + err.message, false);
    error('Cancellation test failed');
  } finally {
    testingCancellation.value = false;
  }
};
</script>
